import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { ActivityType, InitialInterviewPage } from '../../../../enums';
import { InitialInterviewApiService } from '../../initial-interview-api.service';
import { PageService } from '../../service/page-service';
import { TalentHappyPathDataService } from '../../../shared/service/talent-happy-path-data-service';
import { TalentProfileApiService } from '../../../talent-profile/talent-profile-api.service';
import { routeGatherToProfileOrThankYou } from '../../util';
import { VideoInterviewWorkflow } from '../video-interview-workflow.enum';

export type ResolvedWorkflow = 'A' | 'B' | null;

/**
 * Owns the THP video interview A/B decision and the transitions into/out of the video interview
 * pages (BIZ-40408). The video pages live inside the initial-interview module and are shown via the
 * container's page switch, so this service drives the same PageService the rest of the module uses.
 *
 * Insertion invariant:
 *  - Workflow A: video shown when the talent LEAVES the IIQ stage — after completing the questions
 *    or on "I'll do this later" (Submit -> talent profile).
 *  - Workflow B: video shown after pay transparency and before the IIQ questions begin
 *    (Submit -> resume into the IIQ questions).
 */
@Injectable({ providedIn: 'root' })
export class VideoInterviewFlowService {

  private shown = false;
  private resumePage: InitialInterviewPage = InitialInterviewPage.QUESTIONS_PAGE;

  constructor(private pageService: PageService,
              private dataService: TalentHappyPathDataService,
              private router: Router,
              private talentProfileApiService: TalentProfileApiService,
              private initialInterviewApi: InitialInterviewApiService) {}

  /**
   * Resets the "shown" guard, so the step is offered once per flow entry. Completion is not tracked
   * here — the landing page reads it from the service each time.
   */
  reset(): void {
    this.shown = false;
  }

  /**
   * @return the effective A/B workflow for this talent + posting, or null when no video should show
   *         (feature off, country not allow-listed, test cohort on and this talent not in it or not
   *         identifiable, the container's re-read of these fields failed or timed out, workflow
   *         config unset/unrecognized, or a split-by-talent-id workflow with no talent id).
   */
  getWorkflow(): ResolvedWorkflow {
    const posting = this.dataService.postingInfo;
    if (!posting || !posting.videoInterviewEnabled || posting.videoInterviewWorkflow == null) {
      return null;
    }
    switch (posting.videoInterviewWorkflow) {
      case VideoInterviewWorkflow.WorkflowA:
        return 'A';
      case VideoInterviewWorkflow.WorkflowB:
        return 'B';
      case VideoInterviewWorkflow.SplitByTalentId: {
        // Even talent id -> Workflow A, odd -> Workflow B (randomizes the A/B sample).
        const personId = this.dataService.talent?.personId;
        if (personId == null) {
          return null;
        }
        return personId % 2 === 0 ? 'A' : 'B';
      }
      default:
        return null;
    }
  }

  /** Workflow A: the video interview is shown when leaving the IIQ stage (questions done or skipped). */
  shouldShowForWorkflowA(): boolean {
    return !this.shown && this.getWorkflow() === 'A';
  }

  /** Workflow B: the video interview is shown first, as the entry to the initial-interview flow. */
  shouldShowForWorkflowB(): boolean {
    return !this.shown && this.getWorkflow() === 'B';
  }

  /**
   * Enters the video interview step, switching the page on this tick deliberately. Callers submit
   * answers or save a pay rate immediately before calling this and leave their button live, so
   * anything asynchronous here keeps that button on screen over a round trip and a second click
   * re-runs it. That is why the already-recorded check lives on the landing page instead.
   *
   * @param resumePage the IIQ page to resume to after the interview (Workflow B only)
   */
  enterVideoInterview(resumePage: InitialInterviewPage = InitialInterviewPage.QUESTIONS_PAGE): void {
    this.shown = true;
    this.resumePage = resumePage;
    this.pageService.setActivePage(InitialInterviewPage.VIDEO_INTERVIEW_LANDING);
  }

  /**
   * Leaves the step without offering it, for a talent who has already recorded. Not
   * `doThisLater()`: they declined nothing, so this must not be counted as a skip.
   */
  skipVideoInterview(): void {
    this.leaveVideoInterview();
  }

  /** Landing page "Start video interview" -> video interview page. */
  startVideoInterview(): void {
    this.logStartedActivity();
    this.pageService.setActivePage(InitialInterviewPage.VIDEO_INTERVIEW);
  }

  /** Video "Submit Interview" -> next step (Workflow B: resume the IIQ; Workflow A: the profile). */
  submitInterview(): void {
    this.leaveVideoInterview();
  }

  /**
   * Video "I'll do this later" -> the same next step as submit (B: resume the IIQ; A: the profile).
   * The activity is logged here, not at the two buttons, so a skip is counted once wherever it
   * happens.
   */
  doThisLater(): void {
    this.logDoThisLaterActivity();
    this.leaveVideoInterview();
  }

  /** Mirrors IIQ_DO_THIS_LATER_CLICKED. Fire-and-forget: analytics must not block the funnel. */
  private logDoThisLaterActivity(): void {
    const personId = this.dataService.talent?.personId;
    if (personId == null) {
      return;
    }
    this.initialInterviewApi.createTalentProfileActivity(
      ActivityType.VIDEO_INTERVIEW_DO_THIS_LATER_CLICKED,
      personId,
      this.dataService.postingInfo?.orderId,
      this.dataService.postingId
    );
  }

  /**
   * BIZ-40529: the "started" funnel activity. Fire-and-forget like logDoThisLaterActivity, and also
   * requires an order: a start with no order cannot be attributed or de-duplicated by the endpoint's
   * fire-once lookup, and an unguarded read of a still-null talent or posting would throw before the
   * page switch and leave the talent on a dead button.
   */
  private logStartedActivity(): void {
    const personId = this.dataService.talent?.personId;
    const orderId = this.dataService.postingInfo?.orderId;
    if (!personId || !orderId) {
      return;
    }
    this.initialInterviewApi.createTalentProfileActivity(
      ActivityType.VIDEO_INTERVIEW_STARTED,
      personId,
      orderId,
      this.dataService.postingId
    );
  }

  private leaveVideoInterview(): void {
    if (this.getWorkflow() === 'B') {
      // In Workflow B the IIQ comes after the video, so continue into it (not the profile).
      this.pageService.setActivePage(this.resumePage);
    } else {
      this.goToTalentProfile();
    }
  }

  /** Navigates to the "start filling your profile" page (gather-aware). */
  goToTalentProfile(): void {
    if (this.dataService.gather) {
      routeGatherToProfileOrThankYou(this.dataService.gather.talentPersonId, this.talentProfileApiService, this.router);
    } else {
      void this.router.navigate(['/talent-profile'], { skipLocationChange: true });
    }
  }
}
